import { createAction, createSlice } from '@reduxjs/toolkit';

export const fetchContactsRequest = createAction('contacts/fetchContactsRequest');
export const addContactRequest = createAction('contacts/addContactRequest');
export const updateContactRequest = createAction('contacts/updateContactRequest');
export const deleteContactRequest = createAction('contacts/deleteContactRequest');
export const fetchSegmentsRequest = createAction('contacts/fetchSegmentsRequest');
export const createSegmentRequest = createAction('contacts/createSegmentRequest');
export const deleteSegmentRequest = createAction('contacts/deleteSegmentRequest');
export const fetchContactStatsRequest = createAction('contacts/fetchContactStatsRequest');
export const importContactsRequest = createAction('contacts/importContactsRequest');

const initialState = {
  contacts: [],
  loading: false,
  loaded: false,
  error: null,
  lastListParams: { search: '', limit: 10 },
  lastEvaluatedKey: null,
  stats: null,
  statsLoading: false,
  statsLoaded: false,
  statsError: null,
  segments: [],
  segmentsLoading: false,
  segmentsLoaded: false,
  segmentsError: null,
  segmentsSaving: false,
  addContactSaving: false,
  addContactSucceeded: false,
  deleteContactSaving: false,
  deleteSegmentSaving: false,
  importContactsSaving: false,
  importContactsSucceeded: false,
};

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    fetchContactsStart(state, action) {
      state.loading = true;
      state.error = null;
      state.lastListParams = action.payload || state.lastListParams;
    },
    fetchContactsSuccess(state, action) {
      state.loading = false;
      state.loaded = true;
      state.contacts = action.payload.contacts || [];
      state.lastEvaluatedKey = action.payload.lastEvaluatedKey ?? null;
      state.error = null;
    },
    fetchContactsFailure(state, action) {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },
    clearContactsError(state) {
      state.error = null;
    },
    upsertContact(state, action) {
      const contact = action.payload;
      const index = state.contacts.findIndex((item) => item.id === contact.id);
      if (index === -1) {
        state.contacts = [contact, ...state.contacts];
        return;
      }
      state.contacts[index] = contact;
    },
    removeContact(state, action) {
      state.contacts = state.contacts.filter((item) => item.id !== action.payload);
    },
    fetchSegmentsStart(state) {
      state.segmentsLoading = true;
      state.segmentsError = null;
    },
    fetchSegmentsSuccess(state, action) {
      state.segmentsLoading = false;
      state.segmentsLoaded = true;
      state.segments = action.payload || [];
      state.segmentsError = null;
    },
    fetchSegmentsFailure(state, action) {
      state.segmentsLoading = false;
      state.segmentsLoaded = true;
      state.segmentsError = action.payload;
    },
    createSegmentStart(state) {
      state.segmentsSaving = true;
    },
    createSegmentFinish(state) {
      state.segmentsSaving = false;
    },
    removeSegment(state, action) {
      state.segments = state.segments.filter((item) => item.id !== action.payload);
    },
    addContactStart(state) {
      state.addContactSaving = true;
      state.addContactSucceeded = false;
    },
    addContactSuccess(state) {
      state.addContactSaving = false;
      state.addContactSucceeded = true;
    },
    addContactFailure(state) {
      state.addContactSaving = false;
      state.addContactSucceeded = false;
    },
    clearAddContactSucceeded(state) {
      state.addContactSucceeded = false;
    },
    deleteContactStart(state) {
      state.deleteContactSaving = true;
    },
    deleteContactFinish(state) {
      state.deleteContactSaving = false;
    },
    deleteSegmentStart(state) {
      state.deleteSegmentSaving = true;
    },
    deleteSegmentFinish(state) {
      state.deleteSegmentSaving = false;
    },
    fetchContactStatsStart(state) {
      state.statsLoading = true;
      state.statsError = null;
    },
    fetchContactStatsSuccess(state, action) {
      state.statsLoading = false;
      state.statsLoaded = true;
      state.stats = action.payload;
      state.statsError = null;
    },
    fetchContactStatsFailure(state, action) {
      state.statsLoading = false;
      state.statsLoaded = true;
      state.statsError = action.payload;
    },
    importContactsStart(state) {
      state.importContactsSaving = true;
      state.importContactsSucceeded = false;
    },
    importContactsSuccess(state) {
      state.importContactsSaving = false;
      state.importContactsSucceeded = true;
    },
    importContactsFailure(state) {
      state.importContactsSaving = false;
      state.importContactsSucceeded = false;
    },
    clearImportContactsSucceeded(state) {
      state.importContactsSucceeded = false;
    },
  },
});

export const {
  fetchContactsStart,
  fetchContactsSuccess,
  fetchContactsFailure,
  clearContactsError,
  upsertContact,
  removeContact,
  fetchSegmentsStart,
  fetchSegmentsSuccess,
  fetchSegmentsFailure,
  createSegmentStart,
  createSegmentFinish,
  removeSegment,
  addContactStart,
  addContactSuccess,
  addContactFailure,
  clearAddContactSucceeded,
  deleteContactStart,
  deleteContactFinish,
  deleteSegmentStart,
  deleteSegmentFinish,
  fetchContactStatsStart,
  fetchContactStatsSuccess,
  fetchContactStatsFailure,
  importContactsStart,
  importContactsSuccess,
  importContactsFailure,
  clearImportContactsSucceeded,
} = contactsSlice.actions;

export default contactsSlice.reducer;
